import type { Table } from 'dexie';
import type { Effect } from '@abastece/core';
import { db, indicesDeVenta, saldoDiarioId, type Cambio, type VentaLocal } from './dexie.js';

/**
 * Applies reducer output to this device's tables, or to the replay's copy of them in memory, with
 * the same rev-guarded last-write-wins rule the server uses. Keeping the rule identical on both
 * sides is the entire point of sharing the reducers: the moment a phone and the server disagree
 * about which write won, the books and the shop floor stop matching and there is no way to tell
 * which one is lying.
 */

/** What applying asks of a table: the device's own, or one held in memory for a whole replay. */
interface TablaAplicable<T, K> {
  readonly name: string;
  get(clave: K): Promise<T | undefined>;
  put(fila: T): Promise<K>;
  delete(clave: K): Promise<unknown>;
}

type Proyectadas = Pick<
  typeof db,
  | 'usuarios' | 'dispositivos' | 'config' | 'asientos' | 'saldosDiarios' | 'productos' | 'unidades' | 'listas' | 'precios'
  | 'proveedores' | 'existencias' | 'lotes' | 'compras' | 'sesiones' | 'ventas' | 'saldos' | 'recargas'
  | 'reempaques' | 'conteos' | 'salidas' | 'traslados' | 'empleados' | 'planillas' | 'pagosCotizaciones' | 'pagosAguinaldo' | 'ofertas'
  | 'abonos' | 'incobrables' | 'depositos' | 'transferenciasBanco' | 'devoluciones' | 'devolucionesCompra' | 'gastos' | 'movimientosDueno'
  | 'clientes' | 'pedidos' | 'jornadas'
>;

/**
 * The tables effects are written to: this device's database (`db`), or the replay's copy in
 * memory (`db/memoria.ts`). One switch writes to either, so which write wins is one rule.
 */
export type Tablas = {
  readonly [N in keyof Proyectadas]: Proyectadas[N] extends Table<infer T, infer K>
    ? TablaAplicable<T, K>
    : never;
};

/** The rev guard, in one place: a row only accepts a write at least as new as its own. */
async function guardado<T extends { rev: number }>(
  tabla: { get(id: string): Promise<T | undefined>; put(row: T): Promise<unknown> },
  id: string,
  row: T,
): Promise<void> {
  const actual = await tabla.get(id);
  if (!actual || actual.rev <= row.rev) await tabla.put(row);
}

async function parcheado<T extends { rev: number }>(
  tabla: { get(id: string): Promise<T | undefined>; put(row: T): Promise<unknown> },
  id: string,
  rev: number,
  patch: Partial<T>,
): Promise<void> {
  const actual = await tabla.get(id);
  if (actual && actual.rev <= rev) await tabla.put({ ...actual, ...patch, rev });
}

/** Adds one journal line to its account's day (`SaldoDiario`). */
async function sumarAlDia(
  t: Tablas,
  cuenta: string,
  fecha: string,
  debe: number,
  haber: number,
  /** A dollar account's line's dollars (`JournalLine.dolares`), on the side of its córdobas. */
  dolares = 0,
): Promise<void> {
  const id = saldoDiarioId(cuenta, fecha);
  const dia = await t.saldosDiarios.get(id);
  const enDolares = (dia?.dolares ?? 0) + (debe > 0 ? dolares : -dolares);
  await t.saldosDiarios.put({
    id,
    cuenta,
    fecha,
    debe: (dia?.debe ?? 0) + debe,
    haber: (dia?.haber ?? 0) + haber,
    lineas: (dia?.lineas ?? 0) + 1,
    ...(dia?.dolares !== undefined || dolares !== 0 ? { dolares: enDolares } : {}),
  });
}

/** Applies one effect. A journal entry answers with the key it was stored under. */
async function aplicarEfecto(t: Tablas, effect: Effect): Promise<number | undefined> {
  switch (effect.kind) {
    case 'usuario.upsert': {
      const existing = await t.usuarios.get(effect.id);
      if (!existing || existing.rev <= effect.row.rev) await t.usuarios.put(effect.row);
      break;
    }
    case 'usuario.patch': {
      const existing = await t.usuarios.get(effect.id);
      if (existing && existing.rev <= effect.rev) {
        await t.usuarios.put({ ...existing, ...effect.patch, rev: effect.rev });
      }
      break;
    }
    case 'dispositivo.upsert': {
      const existing = await t.dispositivos.get(effect.id);
      if (!existing || existing.rev <= effect.row.rev) await t.dispositivos.put(effect.row);
      break;
    }
    case 'dispositivo.patch': {
      const existing = await t.dispositivos.get(effect.id);
      if (existing && existing.rev <= effect.rev) {
        await t.dispositivos.put({ ...existing, ...effect.patch, rev: effect.rev });
      }
      break;
    }
    case 'config.set': {
      const existing = await t.config.get(effect.clave);
      if (!existing || existing.rev <= effect.rev) {
        await t.config.put({ clave: effect.clave, valor: effect.valor, rev: effect.rev });
      }
      break;
    }
    case 'producto.upsert':
      await guardado(t.productos, effect.id, effect.row);
      break;
    case 'producto.patch':
      await parcheado(t.productos, effect.id, effect.rev, effect.patch);
      break;
    case 'unidadVenta.upsert':
      await guardado(t.unidades, effect.id, effect.row);
      break;
    case 'unidadVenta.patch':
      await parcheado(t.unidades, effect.id, effect.rev, effect.patch);
      break;
    case 'unidadVenta.delete':
      await t.unidades.delete(effect.id);
      break;
    case 'listaPrecio.upsert':
      await guardado(t.listas, effect.id, effect.row);
      break;
    case 'listaPrecio.patch':
      await parcheado(t.listas, effect.id, effect.rev, effect.patch);
      break;
    case 'precio.set':
      await guardado(t.precios, effect.id, effect.row);
      break;
    case 'proveedor.upsert':
      await guardado(t.proveedores, effect.id, effect.row);
      break;
    case 'proveedor.patch':
      await parcheado(t.proveedores, effect.id, effect.rev, effect.patch);
      break;
    case 'existencia.set': {
      // Only a count says when the stock was counted; every other write keeps it.
      const antes = await t.existencias.get(effect.id);
      const row = effect.row.contadoEn == null && antes?.contadoEn != null ? { ...effect.row, contadoEn: antes.contadoEn } : effect.row;
      await guardado(t.existencias, effect.id, row);
      break;
    }
    case 'lote.upsert':
      await guardado(t.lotes, effect.id, effect.row);
      break;
    case 'compra.upsert':
      await guardado(t.compras, effect.id, effect.row);
      break;
    case 'compra.patch':
      await parcheado(t.compras, effect.id, effect.rev, effect.patch);
      break;

    case 'sesion.upsert':
      await guardado(t.sesiones, effect.id, effect.row);
      break;
    case 'sesion.patch':
      await parcheado(t.sesiones, effect.id, effect.rev, effect.patch);
      break;
    // Every write to a sale refreshes the fields its indexes need (`indicesDeVenta`).
    case 'venta.upsert': {
      const row: VentaLocal = { ...effect.row, ...indicesDeVenta(effect.row) };
      await guardado(t.ventas, effect.id, row);
      break;
    }
    case 'venta.patch': {
      const actual = await t.ventas.get(effect.id);
      if (actual && actual.rev <= effect.rev) {
        const row = { ...actual, ...effect.patch, rev: effect.rev };
        await t.ventas.put({ ...row, ...indicesDeVenta(row) });
      }
      break;
    }
    case 'saldo.upsert':
      await guardado(t.saldos, effect.id, effect.row);
      break;
    case 'recarga.upsert':
      await guardado(t.recargas, effect.id, effect.row);
      break;
    case 'reempaque.upsert':
      await guardado(t.reempaques, effect.id, effect.row);
      break;
    case 'conteo.upsert':
      await guardado(t.conteos, effect.id, effect.row);
      break;
    case 'salida.upsert':
      await guardado(t.salidas, effect.id, effect.row);
      break;
    case 'traslado.upsert':
      await guardado(t.traslados, effect.id, effect.row);
      break;
    case 'empleado.upsert':
      await guardado(t.empleados, effect.id, effect.row);
      break;
    case 'empleado.patch':
      await parcheado(t.empleados, effect.id, effect.rev, effect.patch);
      break;
    case 'planilla.upsert':
      await guardado(t.planillas, effect.id, effect.row);
      break;
    case 'planilla.patch':
      await parcheado(t.planillas, effect.id, effect.rev, effect.patch);
      break;
    case 'pagoCotizaciones.upsert':
      await guardado(t.pagosCotizaciones, effect.id, effect.row);
      break;
    case 'pagoAguinaldo.upsert':
      await guardado(t.pagosAguinaldo, effect.id, effect.row);
      break;
    case 'pagoAguinaldo.patch':
      await parcheado(t.pagosAguinaldo, effect.id, effect.rev, effect.patch);
      break;
    case 'oferta.upsert':
      await guardado(t.ofertas, effect.id, effect.row);
      break;
    case 'oferta.patch':
      await parcheado(t.ofertas, effect.id, effect.rev, effect.patch);
      break;
    case 'abono.upsert':
      await guardado(t.abonos, effect.id, effect.row);
      break;
    case 'abono.patch':
      await parcheado(t.abonos, effect.id, effect.rev, effect.patch);
      break;
    case 'incobrable.upsert':
      await guardado(t.incobrables, effect.id, effect.row);
      break;
    case 'incobrable.patch':
      await parcheado(t.incobrables, effect.id, effect.rev, effect.patch);
      break;
    case 'deposito.upsert':
      await guardado(t.depositos, effect.id, effect.row);
      break;
    case 'deposito.patch':
      await parcheado(t.depositos, effect.id, effect.rev, effect.patch);
      break;
    case 'transferenciaBanco.upsert':
      await guardado(t.transferenciasBanco, effect.id, effect.row);
      break;
    case 'transferenciaBanco.patch':
      await parcheado(t.transferenciasBanco, effect.id, effect.rev, effect.patch);
      break;
    case 'devolucion.upsert':
      await guardado(t.devoluciones, effect.id, effect.row);
      break;
    case 'devolucionCompra.upsert':
      await guardado(t.devolucionesCompra, effect.id, effect.row);
      break;
    case 'gasto.upsert':
      await guardado(t.gastos, effect.id, effect.row);
      break;
    case 'gasto.patch':
      await parcheado(t.gastos, effect.id, effect.rev, effect.patch);
      break;
    case 'movimientoDueno.upsert':
      await guardado(t.movimientosDueno, effect.id, effect.row);
      break;
    case 'movimientoDueno.patch':
      await parcheado(t.movimientosDueno, effect.id, effect.rev, effect.patch);
      break;
    case 'cliente.upsert':
      await guardado(t.clientes, effect.id, effect.row);
      break;
    case 'cliente.patch':
      await parcheado(t.clientes, effect.id, effect.rev, effect.patch);
      break;
    case 'pedido.upsert':
      await guardado(t.pedidos, effect.id, effect.row);
      break;
    case 'pedido.patch':
      await parcheado(t.pedidos, effect.id, effect.rev, effect.patch);
      break;
    case 'jornada.upsert':
      await guardado(t.jornadas, effect.id, effect.row);
      break;
    case 'jornada.patch':
      await parcheado(t.jornadas, effect.id, effect.rev, effect.patch);
      break;

    case 'asiento': {
      const { entry } = effect;
      const id = await t.asientos.put({
        eventId: entry.eventId,
        fecha: entry.fecha,
        concepto: entry.concepto,
        lineas: entry.lineas.map((l) => ({
          cuenta: l.cuenta,
          debe: l.debe,
          haber: l.haber,
          ...(l.memo ? { memo: l.memo } : {}),
          ...(l.dolares ? { dolares: l.dolares } : {}),
        })),
      });
      for (const l of entry.lineas) await sumarAlDia(t, l.cuenta, entry.fecha, l.debe, l.haber, l.dolares ?? 0);
      return id;
    }
  }
  return undefined;
}

/**
 * Applies an event's effects. With `conDeshacer` it also answers, in order, what each row it
 * changed was before, so `deshacerCambios` can take the event back (see `db/aplicar.ts`).
 */
export async function aplicarEfectos(
  effects: readonly Effect[],
  conDeshacer: boolean,
  tablas: Tablas = db,
): Promise<Cambio[]> {
  const cambios: Cambio[] = [];
  for (const effect of effects) {
    const fila = conDeshacer ? filaDe(tablas, effect) : null;
    if (fila) {
      cambios.push({ tipo: 'fila', tabla: fila.tabla.name, clave: fila.clave, antes: (await fila.tabla.get(fila.clave)) ?? null });
    }
    // A journal entry adds to each of its accounts' days, and taking it back takes that back too.
    if (conDeshacer && effect.kind === 'asiento') {
      const dias = new Set(effect.entry.lineas.map((l) => saldoDiarioId(l.cuenta, effect.entry.fecha)));
      for (const clave of dias) {
        cambios.push({ tipo: 'fila', tabla: tablas.saldosDiarios.name, clave, antes: (await tablas.saldosDiarios.get(clave)) ?? null });
      }
    }
    const asiento = await aplicarEfecto(tablas, effect);
    if (conDeshacer && asiento !== undefined) cambios.push({ tipo: 'asiento', id: asiento });
  }
  return cambios;
}

/** Puts back what `aplicarEfectos` changed, the last change first. */
export async function deshacerCambios(cambios: readonly Cambio[]): Promise<void> {
  for (let i = cambios.length - 1; i >= 0; i--) {
    const c = cambios[i]!;
    if (c.tipo === 'asiento') await db.asientos.delete(c.id);
    else if (c.antes === null) await db.table(c.tabla).delete(c.clave);
    else await db.table(c.tabla).put(c.antes);
  }
}

interface Fila {
  tabla: { name: string; get(clave: string): Promise<unknown> };
  clave: string;
}

/**
 * The row an effect writes. Every kind is listed, so a new one cannot be applied without saying
 * how to take it back: the compiler refuses a kind this switch does not return for.
 */
function filaDe(t: Tablas, effect: Effect): Fila | null {
  switch (effect.kind) {
    case 'usuario.upsert':
    case 'usuario.patch':
      return { tabla: t.usuarios, clave: effect.id };
    case 'dispositivo.upsert':
    case 'dispositivo.patch':
      return { tabla: t.dispositivos, clave: effect.id };
    case 'config.set':
      return { tabla: t.config, clave: effect.clave };
    case 'producto.upsert':
    case 'producto.patch':
      return { tabla: t.productos, clave: effect.id };
    case 'unidadVenta.upsert':
    case 'unidadVenta.patch':
    case 'unidadVenta.delete':
      return { tabla: t.unidades, clave: effect.id };
    case 'listaPrecio.upsert':
    case 'listaPrecio.patch':
      return { tabla: t.listas, clave: effect.id };
    case 'precio.set':
      return { tabla: t.precios, clave: effect.id };
    case 'proveedor.upsert':
    case 'proveedor.patch':
      return { tabla: t.proveedores, clave: effect.id };
    case 'existencia.set':
      return { tabla: t.existencias, clave: effect.id };
    case 'lote.upsert':
      return { tabla: t.lotes, clave: effect.id };
    case 'compra.upsert':
    case 'compra.patch':
      return { tabla: t.compras, clave: effect.id };
    case 'sesion.upsert':
    case 'sesion.patch':
      return { tabla: t.sesiones, clave: effect.id };
    case 'venta.upsert':
    case 'venta.patch':
      return { tabla: t.ventas, clave: effect.id };
    case 'saldo.upsert':
      return { tabla: t.saldos, clave: effect.id };
    case 'recarga.upsert':
      return { tabla: t.recargas, clave: effect.id };
    case 'reempaque.upsert':
      return { tabla: t.reempaques, clave: effect.id };
    case 'conteo.upsert':
      return { tabla: t.conteos, clave: effect.id };
    case 'salida.upsert':
      return { tabla: t.salidas, clave: effect.id };
    case 'traslado.upsert':
      return { tabla: t.traslados, clave: effect.id };
    case 'empleado.upsert':
    case 'empleado.patch':
      return { tabla: t.empleados, clave: effect.id };
    case 'planilla.upsert':
    case 'planilla.patch':
      return { tabla: t.planillas, clave: effect.id };
    case 'pagoCotizaciones.upsert':
      return { tabla: t.pagosCotizaciones, clave: effect.id };
    case 'pagoAguinaldo.upsert':
    case 'pagoAguinaldo.patch':
      return { tabla: t.pagosAguinaldo, clave: effect.id };
    case 'oferta.upsert':
    case 'oferta.patch':
      return { tabla: t.ofertas, clave: effect.id };
    case 'abono.upsert':
    case 'abono.patch':
      return { tabla: t.abonos, clave: effect.id };
    case 'incobrable.upsert':
    case 'incobrable.patch':
      return { tabla: t.incobrables, clave: effect.id };
    case 'deposito.upsert':
    case 'deposito.patch':
      return { tabla: t.depositos, clave: effect.id };
    case 'transferenciaBanco.upsert':
    case 'transferenciaBanco.patch':
      return { tabla: t.transferenciasBanco, clave: effect.id };
    case 'devolucion.upsert':
      return { tabla: t.devoluciones, clave: effect.id };
    case 'devolucionCompra.upsert':
      return { tabla: t.devolucionesCompra, clave: effect.id };
    case 'gasto.upsert':
    case 'gasto.patch':
      return { tabla: t.gastos, clave: effect.id };
    case 'movimientoDueno.upsert':
    case 'movimientoDueno.patch':
      return { tabla: t.movimientosDueno, clave: effect.id };
    case 'cliente.upsert':
    case 'cliente.patch':
      return { tabla: t.clientes, clave: effect.id };
    case 'pedido.upsert':
    case 'pedido.patch':
      return { tabla: t.pedidos, clave: effect.id };
    case 'jornada.upsert':
    case 'jornada.patch':
      return { tabla: t.jornadas, clave: effect.id };
    // Kept by its key once it is stored: see `aplicarEfectos`.
    case 'asiento':
      return null;
  }
}
